
import { useState, useEffect, useRef, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useDebounce } from 'use-debounce';

export function useDocSearch(engine, containerRef = null) {
  const rawSearchQuery = useSelector((s) => s.codeBook.rawSearchQuery);
  const [debouncedQuery] = useDebounce(rawSearchQuery, 300);
  const [matchCount, setMatchCount] = useState(0);
  const [currentMatch, setCurrentMatch] = useState(0);

  // Iframe engine — register callback, worker calls this when results ready
  // No window.addEventListener('message') needed at all
  useEffect(() => {
    if (engine._type !== 'iframe') return;
    engine._onResults = (count) => {
      setMatchCount(count);
      setCurrentMatch(count > 0 ? 1 : 0);
    };
    return () => { engine._onResults = null; };
  }, [engine]);

  // DOM engine — build index once
  const indexBuilt = useRef(false);
  useEffect(() => {
    if (engine._type !== 'dom') return;
    if (!containerRef?.current || indexBuilt.current) return;
    const t = setTimeout(() => {
      engine.buildIndex(containerRef.current);
      indexBuilt.current = true;
      if (debouncedQuery?.length >= 2) {
        const count = engine.search(debouncedQuery);
        setMatchCount(count);
        setCurrentMatch(count > 0 ? 1 : 0);
      }
    }, 100);
    return () => clearTimeout(t);
  }, [containerRef?.current]);

  const resetIndex = useCallback(() => {
    indexBuilt.current = false;
    engine.clear();
    setMatchCount(0);
    setCurrentMatch(0);
  }, [engine]);

  // Run search when query changes
  useEffect(() => {
    if (debouncedQuery?.length >= 2) {
      if (engine._type === 'dom') {
        if (!indexBuilt.current) return;
        const count = engine.search(debouncedQuery);
        setMatchCount(count);
        setCurrentMatch(count > 0 ? 1 : 0);
      } else {
        engine.search(debouncedQuery);
      }
    } else {
      engine.clear?.();
      setMatchCount(0);
      setCurrentMatch(0);
    }
  }, [debouncedQuery, engine]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (engine._type === 'iframe') {
        engine.destroy?.();
      } else {
        engine.clear?.();
      }
      indexBuilt.current = false;
    };
  }, []);

  // Both engines navigate sync now
  const navigate = useCallback((dir) => {
    const { current, total } = engine.navigate(dir);
    setCurrentMatch(current);
    setMatchCount(total);
  }, [engine]);

  return {
    matchCount,
    currentMatch,
    hasQuery: (debouncedQuery?.length ?? 0) >= 2,
    navigate,
    resetIndex,
  };
}
